import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Grid,
	HStack,
	Icon,
	Link,
	Switch,
	Text,
	Tooltip,
	VStack
} from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { resolveCrmContactId } from '@rta/rta-blueprint-library';

import { isRenderingStale } from '@helpers';
import { lang } from '@lang';
import { RenderingCaptureBox, RtaFeatureButton } from '@components';
import { useProposalDownloadFeature } from '@features/document';
import { useDesign, useTax } from '@hooks';
import { customerState, loadProject, selectNavigation, selectUseTestContact, setUseTestContact, useAppDispatch, useAppSelector } from '@state';
import { useAssociateDealFeature, useFinalizeSaleFeature, useSaveQuoteFeature } from '@features/crm';

import type { JSX } from "react";

const HUBSPOT_PORTAL_ID = '22371204';

const isNonLive = import.meta.env.VITE_APP_ENVIRONMENT !== 'LIVE';

export const CrmActionPanel = (): JSX.Element => {
	const [tax] = useTax();
	const dispatch = useAppDispatch();
	const customer = useAppSelector(customerState);
	const appNav = useAppSelector(selectNavigation);
	const useTestContact = useAppSelector(selectUseTestContact);
	const [, design] = useDesign();

	const saveQuoteFeature = useSaveQuoteFeature();
	const usefinalizeSaleFeature = useFinalizeSaleFeature();
	const associateDealFeature = useAssociateDealFeature();
	const proposal = useProposalDownloadFeature({ tax });

	const { VerificationModal } = saveQuoteFeature.value;
	const { DealModal } = associateDealFeature.value;

	const stale = isRenderingStale({ design });

	const [guardOpen, setGuardOpen] = useState(false);
	const guardCancelRef = useRef<HTMLButtonElement>(null);

	// Gate the proposal download behind a stale-rendering warning, same pattern as the estimate panel
	const proposalGuarded = { ...proposal, handler: () => (stale ? setGuardOpen(true) : proposal.handler()) };

	// appNav.email comes from the URL and is set before the contact lookup resolves, so the toggle
	// still reloads when flipped early — customer.email alone would silently drop the request.
	const handleToggleTestContact = useCallback((checked: boolean): void => {
		dispatch(setUseTestContact(checked));
		const email = appNav.email || customer.email;
		if (email) dispatch(loadProject({ email, useTestContact: checked }));
	}, [appNav.email, customer.email, dispatch]);

	const resolvedContactId = resolveCrmContactId({
		realContactId: customer.contactId,
		environment: import.meta.env.VITE_APP_ENVIRONMENT,
		useTestContact
	});
	const contactUrl = resolvedContactId
		? `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/contact/${resolvedContactId}`
		: null;

	return (
		<>
			<Grid width="100%" height="100%" templateRows="auto 1fr">
				<HStack h="35px" w="full" background="blue.400" px={6} py={2} justifyContent={'center'} mb={2}>
					<Text textAlign={'center'} color={'white'} fontSize={18}>{lang.actionPanels.title.crm}</Text>
					{contactUrl && (
						<Tooltip label="Open contact in HubSpot" placement="bottom">
							<Link href={contactUrl} isExternal display="inline-flex" alignItems="center">
								<Icon as={ExternalLink} color="white" boxSize={4} />
							</Link>
						</Tooltip>
					)}
				</HStack>
				<VStack width="165px" spacing={2} mx="5px" border={'0px'} >
					<RtaFeatureButton width="165px" feature={proposalGuarded} useIcon/>
					<RtaFeatureButton feature={saveQuoteFeature} useIcon/>
					<RtaFeatureButton feature={usefinalizeSaleFeature} useIcon/>
					<RtaFeatureButton feature={associateDealFeature} useIcon/>
					{isNonLive && (
						<Tooltip label="Use the shared dev test contact instead of the real customer contact" placement="left">
							<HStack spacing={2} pt={2}>
								<Switch size="sm" isChecked={useTestContact} onChange={(e) => handleToggleTestContact(e.target.checked)} />
								<Text fontSize="xs" color="gray.500">Test Contact</Text>
							</HStack>
						</Tooltip>
					)}
				</VStack>
			</Grid>
			{VerificationModal}
			{DealModal}
			<AlertDialog
				isOpen={guardOpen}
				leastDestructiveRef={guardCancelRef}
				onClose={() => setGuardOpen(false)}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Rendering may be out of date
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="sm">
								This design changed after its rendering was captured, or has no rendering set. The proposal image may not match the current kitchen.
								Capture a fresh rendering below, or continue anyway.
							</Text>
							{design?.tdController && (
								<Box mt={3}>
									<RenderingCaptureBox
										tdController={design.tdController}
										active={guardOpen}
									/>
								</Box>
							)}
						</AlertDialogBody>
						<AlertDialogFooter gap={2}>
							<Button ref={guardCancelRef} onClick={() => setGuardOpen(false)}>
								Cancel
							</Button>
							<Button
								colorScheme="blue"
								onClick={() => {
									setGuardOpen(false);
									proposal.handler();
								}}
							>
								Continue Anyway
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};
